import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'motion/react';
import {
  MagnifyingGlass,
  ArrowLeft,
  DownloadSimple,
  CheckSquare,
  Square,
  X,
  WarningCircle,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import authedFetch from '../lib/authedFetch';

// Animação de troca de view (coluna direita, com AnimatePresence mode="wait").
// Motion orienta (origem → destino), não decora: os cards da carga inicial não animam.
const viewAnim = {
  initial: { opacity: 0, y: 4 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.2, ease: [0.2, 0, 0, 1] },
};

// Estilos de apresentação (Care DS v3 / kit do CareOS em src/styles/care-ui.css)
const NUM = { fontVariantNumeric: 'tabular-nums lining-nums' };
const TH = 'sticky top-0 z-10 py-[var(--care-cell-padding-y)] px-[var(--care-cell-padding-x)] text-[length:var(--care-text-sm)] font-semibold text-[color:var(--care-text-secondary)] bg-[color:var(--care-bg-surface)] border-b border-[color:var(--care-border-default)]';
const TD = 'px-[var(--care-cell-padding-x)]';
// Linha de tabela não é clicável: sem hover (o CareOS não aplica hover em tr)
const TR = 'h-11 border-b border-[color:var(--care-border-subtle)]';
const ND = 'text-[color:var(--care-neutral-text)]';

// Esqueleto com o brilho do CareOS (.skel); para com prefers-reduced-motion
const SkeletonBar = ({ className = '' }) => (
  <span className={`skel block ${className}`} />
);

// Download do relatório COMPLETO (não só a seleção) — o nome deixa isso claro
const BotaoExcel = ({ onClick, downloading, erro }) => (
  <div className="flex flex-col items-end gap-2">
    <button
      type="button"
      className="ui-botao"
      data-variante="solido"
      onClick={onClick}
      disabled={downloading}
      aria-disabled={downloading || undefined}
      aria-describedby={erro ? 'erro-download' : undefined}
      title="Baixa o relatório completo de patrimônio, com todos os clientes e produtos (não apenas a seleção atual)"
    >
      <DownloadSimple size={16} weight="bold" aria-hidden="true" />
      {downloading ? 'Gerando...' : 'Excel completo (todos)'}
    </button>
    {erro && (
      <p className="ui-aviso" data-tom="negativo" id="erro-download" role="alert">
        <WarningCircle size={16} weight="bold" aria-hidden="true" />
        Não foi possível gerar o Excel.
      </p>
    )}
  </div>
);

const ClientesProdutos = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [produtos, setProdutos] = useState([]);
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [searchTicker, setSearchTicker] = useState('');
  const [clientesProduto, setClientesProduto] = useState([]);
  const [clientesMultiplos, setClientesMultiplos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erroProdutos, setErroProdutos] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [erroClientes, setErroClientes] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [erroDownload, setErroDownload] = useState(false);
  const [dataRelatorio, setDataRelatorio] = useState('');
  // Sequencial da última seleção: respostas de requests antigos são descartadas
  // (antes a última resposta a chegar vencia, mesmo sendo de uma seleção obsoleta)
  const requestSeq = useRef(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    buscarProdutos();
  }, []);

  const buscarProdutos = async () => {
    setErroProdutos(false);
    setLoading(true);
    try {
      const res = await authedFetch(`${API_URL}/api/patrimonio/produtos/lista`);
      if (res.ok) {
        const data = await res.json();
        setProdutos(data.produtos);
        setDataRelatorio(data.data_relatorio);
      } else {
        // Erro distinto do vazio: antes caía em "Nenhum produto encontrado"
        setErroProdutos(true);
      }
    } catch (err) {
      console.error('Erro ao buscar produtos:', err);
      setErroProdutos(true);
    } finally {
      setLoading(false);
    }
  };

  // Busca os clientes da seleção informada (sem alternar a seleção)
  const carregarClientes = async (lista) => {
    setErroClientes(false);
    // Invalida respostas em voo de seleções anteriores
    const reqId = ++requestSeq.current;

    if (lista.length === 0) {
      setClientesProduto([]);
      setClientesMultiplos([]);
      setLoadingClientes(false);
      return;
    }

    setLoadingClientes(true);
    try {
      const res = lista.length === 1
        // Buscar clientes de um único produto
        ? await authedFetch(`${API_URL}/api/patrimonio/produtos/${encodeURIComponent(lista[0])}/clientes`)
        // Buscar clientes com múltiplos produtos
        : await authedFetch(`${API_URL}/api/patrimonio/produtos/multiplos/clientes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(lista)
          });
      if (reqId !== requestSeq.current) return;  // resposta obsoleta: a seleção já mudou
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const json = await res.json();
      if (reqId !== requestSeq.current) return;
      if (lista.length === 1) {
        setClientesProduto(json);
        setClientesMultiplos([]);
      } else {
        setClientesMultiplos(json);
        setClientesProduto([]);
      }
    } catch (err) {
      console.error('Erro:', err);
      // Falha visível: antes a lista velha ficava na tela com a seleção nova
      if (reqId === requestSeq.current) {
        setClientesProduto([]);
        setClientesMultiplos([]);
        setErroClientes(true);
      }
    } finally {
      if (reqId === requestSeq.current) setLoadingClientes(false);
    }
  };

  const toggleProduto = (ticker) => {
    const newSelected = selectedTickers.includes(ticker)
      ? selectedTickers.filter(t => t !== ticker)   // Remover
      : [...selectedTickers, ticker];               // Adicionar

    setSelectedTickers(newSelected);
    return carregarClientes(newSelected);
  };

  // "Tentar de novo" repete a busca da seleção atual (clicar no produto o removeria)
  const recarregarSelecao = () => carregarClientes(selectedTickers);

  const limparSelecao = () => {
    requestSeq.current += 1;  // descarta respostas em voo
    setSelectedTickers([]);
    setClientesProduto([]);
    setClientesMultiplos([]);
    setErroClientes(false);
    setLoadingClientes(false);
  };

  const downloadExcel = async () => {
    setDownloading(true);
    setErroDownload(false);
    try {
      const res = await authedFetch(`${API_URL}/api/patrimonio/download`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `produtos_${dataRelatorio || 'latest'}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setErroDownload(true);
      }
    } catch (err) {
      console.error('Erro:', err);
      setErroDownload(true);
    } finally {
      setDownloading(false);
    }
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  // Abreviado com vírgula pt-BR ("R$ 1,5M", não "R$ 1.5M")
  const formatarMoedaAbreviada = (valor) => {
    const fmt = (v) => v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    if (valor >= 1000000) {
      return `R$ ${fmt(valor / 1000000)}M`;
    } else if (valor >= 1000) {
      return `R$ ${fmt(valor / 1000)}K`;
    }
    return formatarMoeda(valor);
  };

  const produtosFiltrados = produtos.filter(p =>
    p.ticker.toLowerCase().includes(searchTicker.toLowerCase())
  );

  return (
    <MotionConfig reducedMotion="user">
    <div
      className={embedded ? '' : 'ui-pagina'}
      data-largura={embedded ? undefined : 'larga'}
    >
        {/* Header */}
        {!embedded && (
        <header className="ui-tela">
          <div className="ui-tela__texto">
            <button
              type="button"
              className="ui-botao self-start -ml-3"
              data-variante="fantasma"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft size={16} weight="bold" aria-hidden="true" />
              Voltar ao Dashboard
            </button>
            <h1 className="ui-tela__titulo">Clientes e Produtos</h1>
            <p className="ui-tela__sub">
              Selecione produtos para ver quais clientes os possuem
            </p>
            {dataRelatorio && (
              <span className="ui-meta" style={NUM}>
                Dados de: {new Date(dataRelatorio + 'T00:00:00').toLocaleDateString('pt-BR')}
              </span>
            )}
          </div>
        </header>
        )}

        {/* Card de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="ui-card">
            <div className="ui-kpi" data-tamanho="lg">
              <span className="ui-kpi__rotulo">Produtos Únicos</span>
              <span className="ui-kpi__valor">{produtos.length}</span>
            </div>
          </div>

          <div className="ui-card">
            <div className="ui-kpi" data-tamanho="lg">
              <span className="ui-kpi__rotulo">Produtos Selecionados</span>
              <span className="ui-kpi__valor">{selectedTickers.length}</span>
            </div>
          </div>

          <div className="ui-card">
            <div className="ui-kpi" data-tamanho="lg">
              <span className="ui-kpi__rotulo">Clientes Encontrados</span>
              <span className="ui-kpi__valor">
                {selectedTickers.length === 1 ? clientesProduto.length : clientesMultiplos.length}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Coluna Esquerda - Lista de Produtos */}
          <div className="lg:col-span-5">
            <div className="ui-card">
              <div className="ui-cabecalho">
                <h3 className="ui-titulo">Produtos por Volume</h3>
                {selectedTickers.length > 0 && (
                  <div className="ui-cabecalho__acoes">
                    <button
                      type="button"
                      className="ui-botao"
                      data-variante="fantasma"
                      data-tamanho="sm"
                      onClick={limparSelecao}
                    >
                      Limpar ({selectedTickers.length})
                    </button>
                  </div>
                )}
              </div>

              {/* Produtos selecionados */}
              {selectedTickers.length > 0 && (
                <div className="ui-trilho" data-gap="3">
                  {selectedTickers.map(ticker => (
                    <button
                      key={ticker}
                      type="button"
                      className="ui-botao"
                      data-tamanho="sm"
                      data-selecionado
                      onClick={() => toggleProduto(ticker)}
                      aria-label={`Remover ${ticker} da seleção`}
                    >
                      {ticker}
                      <X size={16} weight="bold" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              )}

              {/* Busca */}
              <div className="relative">
                <MagnifyingGlass
                  size={16}
                  weight="bold"
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--care-text-tertiary)]"
                />
                <input
                  type="search"
                  className="ui-campo w-full pl-10"
                  placeholder="Buscar ticker..."
                  aria-label="Buscar ticker"
                  value={searchTicker}
                  onChange={(e) => setSearchTicker(e.target.value)}
                />
              </div>

              <p className="text-[length:var(--care-text-sm)] text-[color:var(--care-text-tertiary)]">
                Clique para selecionar múltiplos produtos (ex: BOVA11, B5P211, LFTS11)
              </p>

              <div className="max-h-[500px] overflow-y-auto" aria-busy={loading}>
                {loading ? (
                  <div className="ui-linhas ui-carregando">
                    <span className="sr-only">Carregando...</span>
                    {[0, 1, 2, 0, 1, 2].map((v, i) => (
                      <div key={i} className="ui-linha" data-i={v || undefined}>
                        <span className="skel ui-carregando__nome" />
                        <span className="skel ui-carregando__meta" />
                      </div>
                    ))}
                  </div>
                ) : erroProdutos ? (
                  <div role="alert" className="flex flex-col items-start gap-3 py-4">
                    <p className="ui-aviso" data-tom="negativo">
                      <WarningCircle size={16} weight="bold" aria-hidden="true" />
                      Não foi possível carregar os produtos.
                    </p>
                    <button
                      type="button"
                      className="ui-botao"
                      data-variante="fio"
                      onClick={buscarProdutos}
                    >
                      Tentar de novo
                    </button>
                  </div>
                ) : produtosFiltrados.length === 0 ? (
                  <p className="ui-vazio text-center py-8">Nenhum produto encontrado</p>
                ) : (
                  <div className="ui-linhas">
                    {produtosFiltrados.slice(0, 100).map((p) => {
                      const isSelected = selectedTickers.includes(p.ticker);
                      return (
                        <button
                          key={p.ticker}
                          type="button"
                          className="ui-linha w-full text-left"
                          data-interativa
                          data-selecionada={isSelected || undefined}
                          aria-pressed={isSelected}
                          onClick={() => toggleProduto(p.ticker)}
                        >
                          <span
                            className={`flex-none inline-flex ${
                              isSelected
                                ? 'text-[color:var(--care-accent-text)]'
                                : 'text-[color:var(--care-text-secondary)]'
                            }`}
                          >
                            {isSelected
                              ? <CheckSquare size={20} weight="fill" aria-hidden="true" />
                              : <Square size={20} aria-hidden="true" />}
                          </span>
                          <span className="ui-linha__nome">{p.ticker}</span>
                          <span className="ui-linha__corpo ui-meta truncate" style={NUM}>
                            {p.estrategia} · {p.num_clientes} cliente(s)
                          </span>
                          <span className="ui-linha__fim care-num text-[length:var(--care-text-sm)] font-medium text-[color:var(--care-text-primary)]">
                            {formatarMoedaAbreviada(p.valor_total)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Coluna Direita - Detalhes dos Clientes (troca de view animada) */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
            {/* Erro ao buscar clientes (falha não fica mais em branco/silenciosa) */}
            {erroClientes && selectedTickers.length > 0 && (
              <motion.div
                key="erro"
                {...viewAnim}
                role="alert"
                className="ui-card"
              >
                <p className="ui-aviso" data-tom="negativo">
                  <WarningCircle size={16} weight="bold" aria-hidden="true" />
                  Não foi possível carregar os clientes desta seleção.
                </p>
                <button
                  type="button"
                  className="ui-botao self-start"
                  data-variante="fio"
                  onClick={recarregarSelecao}
                >
                  Tentar de novo
                </button>
              </motion.div>
            )}

            {/* Carregando clientes da seleção */}
            {!erroClientes && loadingClientes && (
              <motion.div key="carregando" {...viewAnim} className="ui-card" aria-busy="true">
                <span className="sr-only">Carregando clientes...</span>
                <div className="flex flex-col gap-2">
                  <SkeletonBar className="h-6 w-1/2" />
                  <SkeletonBar className="h-4 w-1/3" />
                </div>
                <div className="flex flex-col gap-2">
                  {[0, 1, 2, 3, 4].map(i => (
                    <SkeletonBar key={i} className="h-11 w-full" />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Clientes por Produto Único */}
            {!erroClientes && !loadingClientes && selectedTickers.length === 1 && clientesProduto.length > 0 && (
              <motion.div key={`single-${selectedTickers[0]}`} {...viewAnim} className="ui-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex flex-col gap-1 min-w-0">
                    <h3 className="ui-titulo">
                      Clientes com {selectedTickers[0]}
                    </h3>
                    <p className="ui-paragrafo" style={NUM}>
                      {clientesProduto.length} cliente(s) possuem este ativo
                    </p>
                  </div>
                  <BotaoExcel onClick={downloadExcel} downloading={downloading} erro={erroDownload} />
                </div>

                <div className="overflow-auto max-h-[600px]">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th scope="col" className={`${TH} text-left`}>Cliente</th>
                        <th scope="col" className={`${TH} text-left hidden md:table-cell`}>Conta</th>
                        <th scope="col" className={`${TH} text-center hidden md:table-cell`}>Corretora</th>
                        <th scope="col" className={`${TH} text-right hidden md:table-cell`}>Quantidade</th>
                        <th scope="col" className={`${TH} text-right`}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientesProduto.map((c, idx) => (
                        <tr key={c.conta || idx} className={TR}>
                          <td className={`${TD} text-[length:var(--care-text-base)] text-[color:var(--care-text-primary)]`}>
                            {c.nome}
                            <span className="ui-meta block md:hidden" style={NUM}>
                              {c.conta} · {c.corretora || '—'}
                            </span>
                          </td>
                          <td className={`${TD} hidden md:table-cell text-[length:var(--care-text-base)] text-[color:var(--care-text-secondary)]`} style={NUM}>{c.conta}</td>
                          <td className={`${TD} hidden md:table-cell text-center`}>
                            {c.corretora
                              ? <span className="ui-pilula" data-tom="forte">{c.corretora}</span>
                              : <span className={ND}>—</span>}
                          </td>
                          <td className={`${TD} hidden md:table-cell text-[length:var(--care-text-base)] care-num text-[color:var(--care-text-secondary)]`}>
                            {c.quantidade != null
                              ? c.quantidade.toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                              : <span className={ND}>—</span>}
                          </td>
                          <td className={`${TD} text-[length:var(--care-text-base)] care-num font-medium text-[color:var(--care-text-primary)]`}>
                            {formatarMoeda(c.valor)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Clientes por Múltiplos Produtos */}
            {!erroClientes && !loadingClientes && selectedTickers.length > 1 && clientesMultiplos.length > 0 && (
              <motion.div key="multi" {...viewAnim} className="ui-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex flex-col gap-1 min-w-0">
                    <h3 className="ui-titulo">
                      Clientes com produtos selecionados
                    </h3>
                    <p className="ui-paragrafo" style={NUM}>
                      {clientesMultiplos.length} cliente(s) possuem pelo menos um desses ativos
                    </p>
                  </div>
                  <BotaoExcel onClick={downloadExcel} downloading={downloading} erro={erroDownload} />
                </div>

                <div className="overflow-auto max-h-[600px]">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th scope="col" className={`${TH} text-left`}>Cliente</th>
                        <th scope="col" className={`${TH} text-left hidden md:table-cell`}>Conta</th>
                        <th scope="col" className={`${TH} text-center hidden md:table-cell`}>Produtos</th>
                        <th scope="col" className={`${TH} text-right`}>Valor Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientesMultiplos.map((c, idx) => (
                        <tr key={c.conta || idx} className={TR}>
                          <td className={`${TD} text-[length:var(--care-text-base)] text-[color:var(--care-text-primary)]`}>
                            {c.nome}
                            <span className="ui-meta block md:hidden" style={NUM}>
                              {c.conta} · {Object.keys(c.produtos).join(', ')}
                            </span>
                          </td>
                          <td className={`${TD} hidden md:table-cell text-[length:var(--care-text-base)] text-[color:var(--care-text-secondary)]`} style={NUM}>{c.conta}</td>
                          <td className={`${TD} hidden md:table-cell py-2 text-center`}>
                            <div className="flex flex-wrap gap-1 justify-center">
                              {Object.keys(c.produtos).map(ticker => (
                                <span key={ticker} className="ui-pilula" data-tom="forte">
                                  {ticker}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className={`${TD} text-[length:var(--care-text-base)] care-num font-medium text-[color:var(--care-text-primary)]`}>
                            {formatarMoeda(c.valor_total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* Seleção feita mas sem clientes (lista vazia não fica mais em branco absoluto) */}
            {!erroClientes && !loadingClientes && selectedTickers.length === 1 && clientesProduto.length === 0 && (
              <motion.div key="vazio-single" {...viewAnim} className="ui-card">
                <div className="ui-vazio-bloco" data-area>
                  <p className="ui-vazio">Nenhum cliente com este produto</p>
                </div>
              </motion.div>
            )}
            {!erroClientes && !loadingClientes && selectedTickers.length > 1 && clientesMultiplos.length === 0 && (
              <motion.div key="vazio-multi" {...viewAnim} className="ui-card">
                <div className="ui-vazio-bloco" data-area>
                  <p className="ui-vazio">Nenhum cliente com estes produtos</p>
                </div>
              </motion.div>
            )}

            {/* Estado vazio */}
            {selectedTickers.length === 0 && (
              <motion.div key="sem-selecao" {...viewAnim} className="ui-card">
                <div className="ui-vazio-bloco" data-area>
                  <p className="ui-vazio">Selecione produtos</p>
                  <p className="ui-vazio__dica">
                    Escolha um ou mais produtos da lista para ver quais clientes os possuem
                  </p>
                </div>
              </motion.div>
            )}
            </AnimatePresence>
          </div>
        </div>
    </div>
    </MotionConfig>
  );
};

export default ClientesProdutos;
